import {
	getResourceEntry,
	listDriveForSelectedFolderSilently,
	uploadFile3
} from "../actions/drive";
import { TYPE_AMBIFI_FLOORPLAN_DRAFT } from "../constants/types";
import { getDriveItemsByGuids } from "../drive/driveTreeBuilder";
import { refreshCredentials } from "./securityUtil";
import { v4 as uuidv4 } from "uuid";
import {
	FLOORPLAN_AUTHORING_RENDITIONS_SCHEMA_VERSION,
	FLOORPLAN_DRAFT_SCHEMA_VERSION as IMPORT_DRAFT_SCHEMA_VERSION,
	attachDriveResourcesToDraft,
	serializeFloorplanDraft,
	validateFloorplanDraft
} from "./floorplanImportUtils";

export const FLOORPLAN_DRAFT_TYPE = TYPE_AMBIFI_FLOORPLAN_DRAFT;
export const FLOORPLAN_DRAFT_SCHEMA_VERSION = IMPORT_DRAFT_SCHEMA_VERSION;
export const FLOORPLAN_ASSET_TYPE = "ambifi/floorplan-asset";
export const FLOORPLAN_RENDITION_TYPE = "ambifi/floorplan-rendition";
export const FLOORPLAN_SOURCE_TYPE = "ambifi/floorplan-source";

const DEFAULT_UPLOAD_CONCURRENCY = 3;
const DEFAULT_UPLOAD_ATTEMPTS = 3;
const DEFAULT_RESOURCE_RESOLUTION_ATTEMPTS = 4;
const DRIVE_BATCH_GET_LIMIT = 100;

function asArray(value) {
	return Array.isArray(value) ? value : [];
}

export function joinFloorplanDrivePath(...parts) {
	return parts
		.map(part => `${part || ""}`.trim().replace(/^\/+|\/+$/g, ""))
		.filter(Boolean)
		.join("/");
}

function mimeTypeExtension(mimeType = "") {
	const extensions = {
		"image/png": "png",
		"image/jpeg": "jpg",
		"image/svg+xml": "svg",
		"application/pdf": "pdf"
	};
	return extensions[`${mimeType}`.toLowerCase()] || "bin";
}

export function getFloorplanAssetFileName(asset = {}) {
	const sourceName = `${asset.fileName || ""}`.split(/[\\/]/).pop();
	const sourceExtensionMatch = sourceName.match(/\.([a-z0-9]{1,8})$/i);
	const extension = sourceExtensionMatch ? sourceExtensionMatch[1].toLowerCase() : mimeTypeExtension(asset.mimeType);
	const hash = `${asset.sha256 || ""}`.trim().toLowerCase().replace(/[^a-f0-9]/g, "");
	if (hash) {
		return `${hash}.${extension}`;
	}
	const safeStem = sourceName
		.replace(/\.[^.]+$/, "")
		.replace(/[^a-z0-9._-]+/gi, "-")
		.replace(/^-+|-+$/g, "") || `${asset.id || "floorplan-asset"}`.replace(/[^a-z0-9._-]+/gi, "-");
	return `${safeStem}.${extension}`;
}

function defaultCreateFile(parts, fileName, options) {
	return new File(parts, fileName, options);
}

function assetToFile(asset, createFile) {
	const bytes = asset && asset.bytes;
	if (!(bytes instanceof Uint8Array) && !(bytes instanceof ArrayBuffer)) {
		throw new Error(`Asset ${asset && asset.id ? asset.id : "(unknown)"} has no uploadable byte content.`);
	}
	return createFile([bytes], getFloorplanAssetFileName(asset), {
		type: asset.mimeType || "application/octet-stream"
	});
}

function wait(milliseconds) {
	return new Promise(resolve => setTimeout(resolve, milliseconds));
}

async function withRetry(operation, attempts, sleep) {
	let lastError;
	for (let attempt = 1; attempt <= attempts; attempt++) {
		try {
			return await operation(attempt);
		} catch (error) {
			lastError = error;
			if (attempt < attempts) {
				await sleep(250 * attempt);
			}
		}
	}
	throw lastError;
}

function hasDriveRevision(resource) {
	return !!resource && (Number.isInteger(resource.revision) || (typeof resource.revision === "string" && resource.revision.length > 0));
}

function normalizeResourcePath(path) {
	return (Array.isArray(path) ? path.join("/") : `${path || ""}`).replace(/^\/+|\/+$/g, "");
}

async function fetchDriveResourcesByGuidsInBatches(guids) {
	const batches = [];
	for (let index = 0; index < guids.length; index += DRIVE_BATCH_GET_LIMIT) {
		batches.push(guids.slice(index, index + DRIVE_BATCH_GET_LIMIT));
	}
	const results = await Promise.all(batches.map(batch => getDriveItemsByGuids(batch)));
	return results.reduce((allResources, batch) => allResources.concat(asArray(batch)), []);
}

async function hydrateUploadedDriveResources(uploadedResources, fetchDriveResourcesByGuids) {
	const resources = asArray(uploadedResources);
	const requiringHydration = resources.filter(resource => !hasDriveRevision(resource));
	if (requiringHydration.length === 0) {
		return resources;
	}
	const guids = requiringHydration.map(resource => resource && resource.guid).filter(Boolean);
	if (guids.length !== requiringHydration.length) {
		throw new Error("Drive upload responses did not include a guid for every uploaded floorplan file.");
	}

	const hydratedResources = await fetchDriveResourcesByGuids(Array.from(new Set(guids)));
	const hydratedByGuid = asArray(hydratedResources).reduce((result, resource) => {
		if (resource && resource.guid) {
			result[resource.guid] = resource;
		}
		return result;
	}, {});
	const unresolvedGuids = [];
	const resolved = resources.map(resource => {
		if (hasDriveRevision(resource)) {
			return resource;
		}
		const hydrated = hydratedByGuid[resource.guid];
		if (!hydrated || !hasDriveRevision(hydrated)) {
			unresolvedGuids.push(resource.guid);
			return resource;
		}
		if (resource.orgId && hydrated.orgId && resource.orgId !== hydrated.orgId) {
			throw new Error(`Drive returned floorplan resource ${resource.guid} for a different organization.`);
		}
		const uploadPath = normalizeResourcePath(resource.path);
		const hydratedPath = normalizeResourcePath(hydrated.path);
		if (uploadPath && hydratedPath && uploadPath !== hydratedPath) {
			throw new Error(`Drive returned floorplan resource ${resource.guid} at an unexpected path.`);
		}
		return { ...resource, ...hydrated, path: hydrated.path || resource.path };
	});
	if (unresolvedGuids.length > 0) {
		throw new Error(`Drive has not returned revision metadata for ${unresolvedGuids.length} uploaded floorplan file${unresolvedGuids.length === 1 ? "" : "s"} yet.`);
	}
	return resolved;
}

async function mapWithConcurrency(items, concurrency, worker, onComplete) {
	const results = new Array(items.length);
	let nextIndex = 0;
	let firstError = null;

	async function runWorker() {
		while (!firstError) {
			const index = nextIndex++;
			if (index >= items.length) {
				return;
			}
			try {
				results[index] = await worker(items[index], index);
				if (onComplete) {
					onComplete(results[index], items[index], index);
				}
			} catch (error) {
				firstError = error;
			}
		}
	}

	const workerCount = Math.max(1, Math.min(concurrency, items.length));
	await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
	if (firstError) {
		throw firstError;
	}
	return results;
}

function validationErrorMessage(validation) {
	const errors = asArray(validation && validation.errors);
	const messages = errors.map(error => {
		if (typeof error === "string") {
			return error;
		}
		return `${error && error.path ? `${error.path}: ` : ""}${error && error.message ? error.message : "unknown validation error"}`;
	});
	return messages.join("; ") || "unknown validation error";
}

function makeImportPaths(targetPath, projectFolderName) {
	const projectPath = joinFloorplanDrivePath(targetPath, projectFolderName);
	return {
		projectPath,
		assetPath: joinFloorplanDrivePath(projectPath, "Authoring", "assets"),
		renditionPath: joinFloorplanDrivePath(projectPath, "Authoring", "renditions", "studio"),
		sourcePath: joinFloorplanDrivePath(projectPath, "Sources", "Legacy"),
		draftFileName: `${projectFolderName}.floorplan.json`
	};
}

function resourceForRendition(resourcesByRenditionId, renditionId) {
	if (resourcesByRenditionId instanceof Map) {
		return resourcesByRenditionId.get(renditionId);
	}
	return resourcesByRenditionId && resourcesByRenditionId[renditionId];
}

export function attachStudioRenditionsToDraft(draft, preparedRenditions, resourcesByRenditionId) {
	const renditionsByAssetId = preparedRenditions && preparedRenditions.renditionsByAssetId;
	const sourceAssetIds = renditionsByAssetId && typeof renditionsByAssetId === "object"
		? Object.keys(renditionsByAssetId)
		: [];
	if (sourceAssetIds.length === 0) {
		return draft;
	}
	const profileAssets = {};
	const missingRenditionIds = [];
	for (const sourceAssetId of sourceAssetIds) {
		const descriptor = renditionsByAssetId[sourceAssetId];
		const resource = descriptor && resourceForRendition(resourcesByRenditionId, descriptor.id);
		if (!descriptor || !resource) {
			missingRenditionIds.push(descriptor && descriptor.id ? descriptor.id : sourceAssetId);
			continue;
		}
		profileAssets[sourceAssetId] = {
			id: descriptor.id,
			sha256: descriptor.sha256,
			mimeType: descriptor.mimeType,
			byteLength: descriptor.byteLength,
			fileName: descriptor.fileName,
			width: descriptor.width,
			height: descriptor.height,
			resource: { ...resource }
		};
	}
	if (missingRenditionIds.length > 0) {
		throw new Error(`Missing Drive resource references for floorplan Studio renditions: ${missingRenditionIds.join(", ")}.`);
	}
	return {
		...draft,
		renditions: {
			schemaVersion: FLOORPLAN_AUTHORING_RENDITIONS_SCHEMA_VERSION,
			defaultProfile: "studio",
			profiles: {
				studio: {
					maxDimension: preparedRenditions.inventory && preparedRenditions.inventory.maxDimension,
					assets: profileAssets
				}
			}
		}
	};
}

function dependenciesWithDefaults(overrides = {}) {
	return {
		uploadFile: overrides.uploadFile || uploadFile3,
		refreshCredentials: overrides.refreshCredentials || refreshCredentials,
		refreshDrive: overrides.refreshDrive || listDriveForSelectedFolderSilently,
		generateImportId: overrides.generateImportId || uuidv4,
		createResourceEntry: overrides.createResourceEntry || getResourceEntry,
		fetchDriveResourcesByGuids: overrides.fetchDriveResourcesByGuids || fetchDriveResourcesByGuidsInBatches,
		attachDriveResources: overrides.attachDriveResources || attachDriveResourcesToDraft,
		validateDraft: overrides.validateDraft || validateFloorplanDraft,
		serializeDraft: overrides.serializeDraft || serializeFloorplanDraft,
		createFile: overrides.createFile || defaultCreateFile,
		sleep: overrides.sleep || wait
	};
}

function decoratePartialFailure(error, uploadedCount, projectPath) {
	const decorated = error instanceof Error ? error : new Error(`${error || "Floorplan import failed."}`);
	decorated.uploadedCount = uploadedCount;
	decorated.projectPath = projectPath;
	return decorated;
}

/**
 * Writes one prepared floorplan project to Drive.
 *
 * Binary assets are uploaded with bounded concurrency. The resolved document is
 * then validated and its typed root JSON is written last, making that final
 * Drive object the commit marker for a complete, openable Studio project.
 * Drive-facing functions can be injected through `dependencyOverrides` so the
 * ordering and failure behavior remain straightforward to test.
 */
export async function importPreparedFloorplanProject({
	prepared,
	selectedFile,
	targetPath,
	projectFolderName,
	keepLegacySource = false,
	onProgress = () => {},
	uploadConcurrency = DEFAULT_UPLOAD_CONCURRENCY,
	uploadAttempts = DEFAULT_UPLOAD_ATTEMPTS,
	resourceResolutionAttempts = DEFAULT_RESOURCE_RESOLUTION_ATTEMPTS
}, dependencyOverrides = {}) {
	if (!prepared || !prepared.draft || !Array.isArray(prepared.assets)) {
		throw new Error("A prepared floorplan draft and asset inventory are required.");
	}
	if (!targetPath || !projectFolderName) {
		throw new Error("A Drive target path and project folder name are required.");
	}

	const dependencies = dependenciesWithDefaults(dependencyOverrides);
	const paths = makeImportPaths(targetPath, projectFolderName);
	const importId = dependencies.generateImportId();
	const assets = prepared.assets;
	const preparedRenditions = prepared.renditions || null;
	const renditionAssets = asArray(preparedRenditions && preparedRenditions.assets);
	const resourcesByAssetId = {};
	const uploadedAssetsByAssetId = {};
	const resourcesByRenditionId = {};
	const uploadedRenditionsById = {};
	const supportingUploadCount = assets.length + renditionAssets.length;
	const totalUploads = supportingUploadCount + (keepLegacySource ? 1 : 0) + 1;
	let completed = 0;

	await dependencies.refreshCredentials();
	onProgress({ completed, total: totalUploads, label: "Uploading floorplan layers…" });

	let uploadedResources;
	try {
		uploadedResources = await mapWithConcurrency(
			assets,
			uploadConcurrency,
			async asset => {
				const file = assetToFile(asset, dependencies.createFile);
				const roles = asArray(asset.roles).filter(Boolean);
				return withRetry(() => dependencies.uploadFile(paths.assetPath, file, null, false, {
					deferDriveRefresh: true,
					suppressDriveSpinner: true,
					suppressDriveUploadProgress: true,
					metaData: {
						type: FLOORPLAN_ASSET_TYPE,
						project: projectFolderName,
						import: importId,
						role: roles.length === 1 ? roles[0] : (roles.length ? "multiple" : "layer"),
						roles,
						hash: asset.sha256,
						assetId: asset.id,
						contentType: asset.mimeType || "application/octet-stream"
					}
				}), uploadAttempts, dependencies.sleep);
			},
			(resource, asset) => {
				uploadedAssetsByAssetId[asset.id] = resource;
				completed++;
				onProgress({ completed, total: totalUploads, label: `Uploaded ${completed} of ${supportingUploadCount} floorplan files` });
			}
		);
	} catch (error) {
		throw decoratePartialFailure(error, completed, paths.projectPath);
	}

	assets.forEach((asset, index) => {
		uploadedAssetsByAssetId[asset.id] = uploadedResources[index];
	});

	let uploadedRenditionResources = [];
	if (renditionAssets.length > 0) {
		onProgress({ completed, total: totalUploads, label: "Uploading optimized Studio previews…" });
		try {
			uploadedRenditionResources = await mapWithConcurrency(
				renditionAssets,
				uploadConcurrency,
				async rendition => {
					const file = assetToFile(rendition, dependencies.createFile);
					return withRetry(() => dependencies.uploadFile(paths.renditionPath, file, null, false, {
						deferDriveRefresh: true,
						suppressDriveSpinner: true,
						suppressDriveUploadProgress: true,
						metaData: {
							type: FLOORPLAN_RENDITION_TYPE,
							project: projectFolderName,
							import: importId,
							role: "studio-preview",
							profile: "studio",
							hash: rendition.sha256,
							renditionId: rendition.id,
							sourceAssetIds: asArray(rendition.sourceAssetIds),
							width: rendition.width,
							height: rendition.height,
							contentType: rendition.mimeType || "image/png"
						}
					}), uploadAttempts, dependencies.sleep);
				},
				(resource, rendition) => {
					uploadedRenditionsById[rendition.id] = resource;
					completed++;
					onProgress({ completed, total: totalUploads, label: `Uploaded ${completed} of ${supportingUploadCount} floorplan files` });
				}
			);
		} catch (error) {
			throw decoratePartialFailure(error, completed, paths.projectPath);
		}

		renditionAssets.forEach((rendition, index) => {
			uploadedRenditionsById[rendition.id] = uploadedRenditionResources[index];
		});
	}

	try {
		onProgress({ completed, total: totalUploads, label: "Resolving uploaded Drive revisions…" });
		const hydratedResources = await withRetry(
			() => hydrateUploadedDriveResources(
				uploadedResources.concat(uploadedRenditionResources),
				dependencies.fetchDriveResourcesByGuids
			),
			resourceResolutionAttempts,
			dependencies.sleep
		);
		assets.forEach((asset, index) => {
			resourcesByAssetId[asset.id] = dependencies.createResourceEntry(hydratedResources[index]);
		});
		renditionAssets.forEach((rendition, index) => {
			resourcesByRenditionId[rendition.id] = dependencies.createResourceEntry(hydratedResources[assets.length + index]);
		});
	} catch (error) {
		throw decoratePartialFailure(error, completed, paths.projectPath);
	}

	let draft;
	let validation;
	try {
		draft = dependencies.attachDriveResources(prepared.draft, resourcesByAssetId);
		draft = attachStudioRenditionsToDraft(draft, preparedRenditions, resourcesByRenditionId);
		validation = dependencies.validateDraft(draft);
		if (!validation || validation.valid !== true) {
			throw new Error(`The resolved floorplan draft is invalid: ${validationErrorMessage(validation)}`);
		}
	} catch (error) {
		throw decoratePartialFailure(error, completed, paths.projectPath);
	}

	let sourceResource = null;
	if (keepLegacySource && selectedFile) {
		onProgress({ completed, total: totalUploads, label: "Archiving the original project…" });
		try {
			sourceResource = await withRetry(() => dependencies.uploadFile(paths.sourcePath, selectedFile, null, false, {
				deferDriveRefresh: true,
				suppressDriveSpinner: true,
				suppressDriveUploadProgress: true,
				metaData: {
					type: FLOORPLAN_SOURCE_TYPE,
					project: projectFolderName,
					import: importId,
					role: "legacy-source"
				}
			}), uploadAttempts, dependencies.sleep);
			completed++;
		} catch (error) {
			throw decoratePartialFailure(error, completed, paths.projectPath);
		}
	}

	let draftResource;
	try {
		const serializedDraft = dependencies.serializeDraft(draft);
		const draftContent = typeof serializedDraft === "string" ? serializedDraft : JSON.stringify(serializedDraft, null, 2);
		const draftFile = dependencies.createFile([draftContent], paths.draftFileName, { type: "application/json;charset=utf-8" });
		onProgress({ completed, total: totalUploads, label: "Creating the Floorplan Studio document…" });
		draftResource = await withRetry(() => dependencies.uploadFile(paths.projectPath, draftFile, null, false, {
			deferDriveRefresh: true,
			suppressDriveSpinner: true,
			suppressDriveUploadProgress: true,
			metaData: {
				type: FLOORPLAN_DRAFT_TYPE,
				schemaVersion: FLOORPLAN_DRAFT_SCHEMA_VERSION,
				project: projectFolderName,
				import: importId,
				assetCount: assets.length,
				renditionCount: renditionAssets.length,
				sourceFormat: "claude-floorplan-project",
				...(sourceResource && sourceResource.guid ? { sourceArchiveGuid: sourceResource.guid } : {})
			}
		}), uploadAttempts, dependencies.sleep);
	} catch (error) {
		throw decoratePartialFailure(error, completed, paths.projectPath);
	}

	completed++;
	onProgress({ completed, total: totalUploads, label: "Refreshing Drive…" });
	try {
		await dependencies.refreshDrive();
	} catch (error) {
		// The root object is already committed. A later Drive refresh will reveal it.
		console.warn("Floorplan imported, but Drive could not be refreshed", error);
	}

	return {
		resource: draftResource,
		draft,
		assetResources: resourcesByAssetId,
		uploadedAssetResources: uploadedAssetsByAssetId,
		renditionResources: resourcesByRenditionId,
		uploadedRenditionResources: uploadedRenditionsById,
		sourceResource,
		validation,
		importId,
		projectPath: paths.projectPath
	};
}

export default importPreparedFloorplanProject;
